import ArrowElement from "@/assets/images/ArrowElement.svg";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { MenuItem, styled } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useState } from "react";
import api from "@/services/api";
import { EmployeeGroup, IdText, OrganizationUnit, PermissionName } from "@/models/apiModels";
import popupService from "@/services/popupService";
import {
    unstable_Blocker as Blocker,
    unstable_BlockerFunction as BlockerFunction,
    useBlocker,
    useNavigate,
    useParams,
} from "react-router-dom";
import { EditEmployeeRequest, Employee } from "@/models/employees";
import { Gender } from "@/models/enums";
import { useTranslation } from "react-i18next";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import authStore from "@/stores/authStore";
import EmployeePageButtons from "../employeePageButtons/employeePageButtons";
import "./employeeGeneralInfo.css";
import dayjs from "dayjs";
import StyledSwitch from "@/components/common/baseSwitch";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import {
    checkEditPermissionWithoutAlert,
    checkPermissionsWithAlert,
    formatDate,
    formatDateTime,
    validateEmployeeDates,
} from "@/util";
import { useLocation } from "react-router-dom";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const StyledButton = styled(ButtonUnstyled)(
    () => `
        &:hover {
          background-color: #222C4B;
          display: flex;
          align-items: center;
          gap: 7px;
        }
      
        &.${buttonUnstyledClasses.active} {
          background-color: #222C4B;
          display: flex;
          align-items: center;
          gap: 7px;
        }

        &.${buttonUnstyledClasses.root} {
          display: flex;
          align-items: center;
          gap: 7px;
        }
        
        &.${buttonUnstyledClasses.focusVisible} {
          display: flex;
          align-items: center;
          gap: 7px;
          box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
          outline: none;
        }
      
        &.${buttonUnstyledClasses.disabled} {
          display: flex;
          align-items: center;
          gap: 7px;
          opacity: 0.5;
          cursor: not-allowed;
        }`
);

const EmployeeGeneralInfo = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [editMode, setEditMode] = useState(false);
    const [isSave, setIsSave] = useState(false);
    const [backButtonName, setBackButtonName] = useState("");
    const [employeeData, setEmployeeData] = useState<Employee>({
        firstName: "",
        lastName: "",
        email: "",
        employeeGroup: "",
        employeeNumber: "",
        employeePID: "",
        gender: Gender.Male,
        id: 0,
        isActive: false,
        mobilePhone: "",
        organizationUnit: "",
        workEndDate: "",
        workPhone: "",
        workStartDate: "",
        username: "",
        defaultCurrencyId: 1,
        leftBalance: 0,
        usedBalance: 0,
        updatedAt: "",
        updatedByUser: "",
        quickAccessLink: null,
    });
    const [originalEmployeeData, setOriginalEmployeeData] = useState<Employee>(new Employee());
    const [organizationUnits, setOrganizationUnits] = useState<OrganizationUnit[]>([]);
    const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);
    const genderTypeList: IdText[] = (
        Object.values(Gender).filter((val) => isNaN(Number(val))) as (keyof typeof Gender)[]
    ).map((key) => new IdText(key, t(`gender.${key}`)));

    function handleOnChangeStartDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeData((prevData) => ({
            ...prevData,
            workStartDate: e ? formattedDate : null,
        }));
    }

    function handleOnChangeEndDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeData((prevData) => ({
            ...prevData,
            workEndDate: e ? formattedDate : null,
        }));
    }

    const handleMobilePhoneChange = (event: any) => {
        const inputValue = event.target.value;
        const isValid = /^[0-9+]+$/.test(inputValue);

        if (inputValue === "") {
            setEmployeeData((prevData) => ({
                ...prevData,
                mobilePhone: "",
            }));
        }
        if (isValid) {
            setEmployeeData((prevData) => ({
                ...prevData,
                mobilePhone: inputValue.substring(0, 15),
            }));
        }
    };

    const handleWorkPhoneChange = (event: any) => {
        const inputValue = event.target.value;
        const isValid = /^[0-9+]+$/.test(inputValue);

        if (inputValue === "") {
            setEmployeeData((prevData) => ({
                ...prevData,
                workPhone: "",
            }));
        }
        if (isValid) {
            setEmployeeData((prevData) => ({
                ...prevData,
                workPhone: inputValue.substring(0, 15),
            }));
        }
    };

    function getEmployeeGroup(groupName: string) {
        const group = employeeGroups?.find((group) => group.text === groupName);
        return Number(group?.id);
    }

    function getOrganizationUnit(organizationUnitName: string) {
        const unit = organizationUnits?.find((unit) => unit.text === organizationUnitName);
        return Number(unit?.id);
    }

    async function saveOnClick(event: React.MouseEvent) {
        event.preventDefault();
        popupService.showLoading();
        const validDates = await validateEmployeeDates(employeeData.workStartDate, employeeData.workEndDate);
        if (!validDates) {
            return;
        }

        try {
            const request: EditEmployeeRequest = {
                id: employeeData.id,
                email: employeeData.email,
                employeeNumber: employeeData.employeeNumber,
                employeePID: employeeData.employeePID,
                firstName: employeeData.firstName,
                lastName: employeeData.lastName,
                gender: employeeData.gender,
                isActive: employeeData.isActive,
                mobilePhone: employeeData.mobilePhone,
                workEndDate: employeeData.workEndDate,
                workStartDate: employeeData.workStartDate,
                workPhone: employeeData.workPhone,
                employeeGroupId: getEmployeeGroup(employeeData.employeeGroup),
                organizationUnitId: getOrganizationUnit(employeeData.organizationUnit),
                username: employeeData.username,
            };
            await api.editEmployee(request);
            setIsSave(true);
            await popupService.showSuccess(t("employee.EditMsg")).then(() => {
                navigate("/employees/" + id);
            });
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    function handleCancelEdit() {
        const employeeDataString = JSON.stringify(employeeData);
        const originalEmployeeDataString = JSON.stringify(originalEmployeeData);

        if (editMode && employeeDataString !== originalEmployeeDataString) {
            popupService.wantDiscard(t("general.ChangesMadeNotSavedMsg")).then((result) => {
                if (result.isConfirmed) {
                    setEmployeeData(originalEmployeeData);
                    setEditMode(false);
                }
            });
        } else {
            setEditMode(false);
        }
    }

    const shouldBlock: BlockerFunction = React.useCallback(
        ({ currentLocation, nextLocation }) => {
            const employeeDataString = JSON.stringify(employeeData);
            const originalEmployeeDataString = JSON.stringify(originalEmployeeData);

            return (
                editMode &&
                !isSave &&
                employeeDataString !== originalEmployeeDataString &&
                currentLocation.pathname !== nextLocation.pathname
            );
        },
        [employeeData, originalEmployeeData, isSave, editMode]
    );

    const blocker = useBlocker(shouldBlock);

    const handleShowDiscardMsg = (blocker: Blocker) => {
        if (blocker.state === "blocked") {
            popupService.wantDiscard(t("general.ChangesMadeNotSavedMsg")).then((result) => {
                if (result.isConfirmed) {
                    blocker?.proceed?.();
                    return true;
                } else {
                    blocker?.reset?.();
                    return false;
                }
            });
        }
        return false;
    };

    function sendEmail(email: string) {
        window.location.href = `mailto:${email}`;
    }

    async function getEmployeePageInitData() {
        const employeesListDataResponsePromise = api.getEmployeesPageData();
        const employeesListDataResponse = await employeesListDataResponsePromise;
        setOrganizationUnits(employeesListDataResponse.data.organizationUnits);
        setEmployeeGroups(employeesListDataResponse.data.employeeGroups);
    }

    async function getEmployeeData(employeeId: number | null): Promise<Employee> {
        const id = employeeId;
        const responsePromise = api.getEmployeeData(id);
        return (await responsePromise).data;
    }

    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.EmployeeGeneralInfo)) {
            return;
        }
        popupService.showLoading();
        const data = await getEmployeeData(Number(id));
        await getEmployeePageInitData();
        const apiResponse = data;
        setEmployeeData(apiResponse);
        setOriginalEmployeeData(apiResponse);
        popupService.close();
    }

    function goToPreviousPage() {
        const navigateUrl = location.search?.replace("?returnUrl=", "");
        if (navigateUrl) {
            navigate(navigateUrl!);
        } else navigate("/employees");
    }

    const handleGenerateId = (): string => {
        const array = new Uint32Array(4);
        crypto.getRandomValues(array);
        return array.join("-");
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
            const navigateUrl = location.search?.replace("?returnUrl=", "");
            if (!navigateUrl) {
                setBackButtonName(t("employee.BackToList") as string);
            }
            if (navigateUrl === "/digital-wallet/list") {
                setBackButtonName(t("employee.BackToWalletList") as string);
            }
            if (navigateUrl === "/digital-wallet/management") {
                setBackButtonName(t("employee.BackToWalletManagement") as string);
            }
            if (navigateUrl?.includes("/transactions")) {
                setBackButtonName(t("employee.BackToTransactions") as string);
            }
            if (navigateUrl?.includes("/orders")) {
                setBackButtonName(t("employee.BackToOrders") as string);
            }
            if (navigateUrl?.includes("/employeeOrders")) {
                setBackButtonName(t("general.backToEmployeeOrder") as string);
            }
            if (navigateUrl?.includes("/wallet")) {
                setBackButtonName(t("general.backToEmployeeWallet") as string);
            }
            if (navigateUrl?.includes("/accounting/invoice/")) {
                setBackButtonName(t("general.backToConnectedOrder") as string);
            }
        }
        return () => {
            ignore = true;
        };
    }, []);

    const contentData = !editMode ? (
        <div className="general-info-content">
            <div>
                <div className="general-info-data-header font-style-18">
                    <i className="icon-f icon-PersonalInfoIcon fs-26" />
                    {t("employee.PersonalInfo")}
                </div>
                <div className="general-info-data-view">
                    <div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.LastName")}</div>
                            <div className="font-style-18-bold">{employeeData?.lastName}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.FirstName")}</div>
                            <div className="font-style-18-bold">{employeeData?.firstName}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.Number")}</div>
                            <div className="font-style-18-bold">{employeeData?.employeeNumber}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("user.Username")}</div>
                            <div className="font-style-18-bold">{employeeData?.username}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.IdCard")}</div>
                            <div className="font-style-18-bold">{employeeData?.employeePID}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.Gender")}</div>
                            <div className="font-style-18-bold">
                                {employeeData?.gender && t(`gender.${employeeData?.gender}`)}
                            </div>
                        </div>
                        {employeeData?.quickAccessLink ? (
                            <>
                                <div className="horizontal-divider"></div>
                                <div className="general-info-data-view-row">
                                    <div className="font-style">{t("employee.Card.QuickAccessLink")}</div>
                                    <div className="font-style-18-bold">
                                        <span
                                            className="link-element"
                                            title={employeeData?.quickAccessLink}
                                            onClick={() =>
                                                navigator.clipboard.writeText(employeeData?.quickAccessLink ?? "")
                                            }
                                        >
                                            {t("organization.copyLinkToClipboard")}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            </div>
            <div>
                <div className="general-info-data-header font-style-18">
                    <i className="icon-f icon-WorkPlaceIcon fs-26" />
                    {t("employee.WorkPlace")}
                </div>
                <div className="general-info-data-view">
                    <div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.StartDate")}</div>
                            <div className="font-style-18-bold">
                                {employeeData?.workStartDate ? formatDate(employeeData?.workStartDate ?? "") : ""}
                            </div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.TerminationDate")}</div>
                            <div className="font-style-18-bold">
                                {employeeData?.workEndDate ? formatDate(employeeData?.workEndDate ?? "") : ""}
                            </div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.Department")}</div>
                            <div className="font-style-18-bold">{employeeData?.organizationUnit}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.Group")}</div>
                            <div className="font-style-18-bold">{employeeData?.employeeGroup}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.NewEmpStatus")}</div>
                            <div className="font-style-18-bold">
                                {employeeData?.isActive ? (
                                    <div className="status-element">
                                        <div className="status-color-active"></div>
                                        <div>{t("employee.ActiveStatus")}</div>
                                    </div>
                                ) : (
                                    <div className="status-element">
                                        <div className="status-color-inactive"></div>
                                        <div>{t("employee.InactiveStatus")}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <div className="general-info-data-header font-style-18">
                    <i className="icon-f icon-ContactInfoIcon fs-26" />
                    {t("employee.ContactInfo")}
                </div>
                <div className="general-info-data-view">
                    <div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.MobilePhone")}</div>
                            <div className="font-style-18-bold">{employeeData?.mobilePhone}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.WorkPhone")}</div>
                            <div className="font-style-18-bold">{employeeData?.workPhone}</div>
                        </div>
                        <div className="horizontal-divider"></div>
                        <div className="general-info-data-view-row">
                            <div className="font-style">{t("employee.Card.Email")}</div>
                            <div
                                onClick={() => sendEmail(employeeData.email)}
                                className="font-style-18-bold link-element"
                            >
                                {employeeData?.email}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div className="general-info-content">
            <div>
                <div className="general-info-data-header font-style-18">
                    <i className="icon-f icon-PersonalInfoIcon fs-26" />
                    {t("employee.PersonalInfo")}
                </div>
                <div className="general-info-data-edit">
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.LastName")}
                            size="small"
                            value={employeeData?.lastName}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    lastName: event.target.value,
                                }))
                            }
                            inputProps={{
                                inputMode: "text",
                                maxLength: 50,
                            }}
                            required
                        />
                    </div>
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.FirstName")}
                            size="small"
                            value={employeeData?.firstName}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    firstName: event.target.value,
                                }))
                            }
                            inputProps={{
                                inputMode: "text",
                                maxLength: 50,
                            }}
                            required
                        />
                    </div>
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.EmployeeNumber")}
                            size="small"
                            value={employeeData?.employeeNumber}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    employeeNumber: event.target.value,
                                }))
                            }
                            inputProps={{
                                maxLength: 40,
                            }}
                            required
                        />
                    </div>
                    <div>
                        <BaseInput
                            id={handleGenerateId()}
                            fullWidth
                            label={t("user.Username")}
                            size="small"
                            value={employeeData?.username}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    username: event.target.value,
                                }))
                            }
                            inputProps={{
                                inputMode: "text",
                                maxLength: 100,
                            }}
                            required
                        />
                    </div>
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.IdCard")}
                            size="small"
                            type="number"
                            value={employeeData?.employeePID}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    employeePID: event.target.value,
                                }))
                            }
                            inputProps={{
                                inputMode: "numeric",
                                maxLength: 9,
                            }}
                            required
                        />
                    </div>
                    <div>
                        <BaseInput
                            select
                            label={t("employee.Gender")}
                            size="small"
                            value={employeeData?.gender ?? ""}
                            autoComplete="new-password"
                            sx={{
                                width: "100%",
                            }}
                            onChange={(event: any) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    gender: event.target.value,
                                }))
                            }
                        >
                            {genderTypeList.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.text}
                                </MenuItem>
                            ))}
                        </BaseInput>
                    </div>
                </div>
            </div>
            <div>
                <div className="general-info-data-header font-style-18">
                    <i className="icon-f icon-WorkPlaceIcon fs-26" />
                    {t("employee.WorkPlace")}
                </div>
                <div className="general-info-data-edit">
                    <div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                                className="date-picker-input"
                                label={t("employee.WorkStartDate")}
                                inputFormat="DD/MM/YYYY"
                                value={employeeData?.workStartDate}
                                onChange={(event) => handleOnChangeStartDate(event)}
                                renderInput={(params) => <BaseDateInput autoComplete="new-password" {...params} />}
                            />
                        </LocalizationProvider>
                    </div>
                    <div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                                className="date-picker-input"
                                label={t("employee.WorkEndDate")}
                                inputFormat="DD/MM/YYYY"
                                value={employeeData?.workEndDate}
                                onChange={(event) => handleOnChangeEndDate(event)}
                                renderInput={(params) => <BaseDateInput autoComplete="new-password" {...params} />}
                            />
                        </LocalizationProvider>
                    </div>
                    <div>
                        <BaseInput
                            select
                            label={t("employee.Department")}
                            value={employeeData?.organizationUnit}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    organizationUnit: event.target.value.toString(),
                                }))
                            }
                            size="small"
                            sx={{
                                width: "100%",
                            }}
                        >
                            {organizationUnits?.map((organizationUnit) => (
                                <MenuItem key={organizationUnit.id} value={organizationUnit.text}>
                                    {organizationUnit.text}
                                </MenuItem>
                            ))}
                        </BaseInput>
                    </div>
                    <div>
                        <BaseInput
                            select
                            label={t("employee.EmployeeGroup")}
                            size="small"
                            sx={{
                                width: "100%",
                            }}
                            value={employeeData?.employeeGroup}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    employeeGroup: event.target.value.toString(),
                                }))
                            }
                            required
                        >
                            {employeeGroups.map((employeeGroup) => (
                                <MenuItem key={employeeGroup.id} value={employeeGroup.text}>
                                    {employeeGroup.text}
                                </MenuItem>
                            ))}
                        </BaseInput>
                    </div>
                    <div className="create-employee-status font-style-16">
                        <span>{t("employee.NewEmpStatus")}</span>
                        <StyledSwitch
                            value={employeeData?.isActive}
                            checked={employeeData?.isActive}
                            onChange={() =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    isActive: !employeeData.isActive,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>
            <div>
                <div className="general-info-data-header font-style-18">
                    <i className="icon-f icon-ContactInfoIcon fs-26" />
                    {t("employee.ContactInfo")}
                </div>
                <div className="general-info-data-edit">
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.MobilePhone")}
                            size="small"
                            value={employeeData?.mobilePhone}
                            autoComplete="new-password"
                            onChange={(event) => handleMobilePhoneChange(event)}
                            inputProps={{
                                inputMode: "tel",
                                maxLength: 15,
                            }}
                        />
                    </div>
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.WorkPhone")}
                            size="small"
                            value={employeeData?.workPhone}
                            autoComplete="new-password"
                            onChange={(event) => handleWorkPhoneChange(event)}
                            inputProps={{
                                inputMode: "tel",
                                maxLength: 15,
                            }}
                        />
                    </div>
                    <div>
                        <BaseInput
                            fullWidth
                            label={t("employee.Email")}
                            size="small"
                            value={employeeData?.email}
                            autoComplete="new-password"
                            onChange={(event) =>
                                setEmployeeData((prevData) => ({
                                    ...prevData,
                                    email: event.target.value,
                                }))
                            }
                            inputProps={{
                                inputMode: "email",
                                maxLength: 255,
                            }}
                            required
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const pageButtons = !editMode ? (
        <div className="general-info-header-right">
            <StyledButton className="basic-button" onClick={() => goToPreviousPage()}>
                <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                <div>{backButtonName}</div>
            </StyledButton>
            {checkEditPermissionWithoutAlert(PermissionName.EmployeeGeneralInfo) ? (
                <StyledButton
                    style={{ padding: "0px 50px" }}
                    onClick={() => setEditMode(true)}
                    className="edit-save-button"
                >
                    {t("button.edit")}
                </StyledButton>
            ) : (
                ""
            )}
        </div>
    ) : (
        <div className="general-info-header-right">
            <StyledButton style={{ padding: "0px 50px" }} onClick={handleCancelEdit} className="cancel-button">
                {t("button.cancel")}
            </StyledButton>
            <StyledButton
                style={{ padding: "0px 50px" }}
                className="edit-save-button"
                onClick={(event) => saveOnClick(event)}
            >
                {t("button.save")}
            </StyledButton>
        </div>
    );

    const aboutUpdated = employeeData?.updatedByUser && (
        <div className="general-info-about-updated">
            <div className="font-style-18-bold" style={{ opacity: "0.7" }}>
                {t("employee.HistoryChanges")}:
            </div>
            <div className="font-style-18-bold">
                {employeeData.updatedByUser + " " + formatDateTime(employeeData?.updatedAt)}
            </div>
            <hr />
        </div>
    );

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {blocker ? handleShowDiscardMsg(blocker) : null}
            <div className="general-info-container">
                <div className="general-info-header">
                    <EmployeePageButtons />
                    {pageButtons}
                </div>
                <div>{contentData}</div>
            </div>
            {aboutUpdated}
        </CacheProvider>
    );
};

export default EmployeeGeneralInfo;
